import React, { useState, useEffect, useMemo } from 'react';
import {
  Container,
  Paper,
  Typography,
  Box,
  Card,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Button,
  Alert,
  CircularProgress,
  Checkbox,
  Snackbar,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Tabs,
  Tab,
  Chip,
  IconButton,
  Tooltip,
  InputAdornment,
  TablePagination,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  FormControlLabel,
  Grid,
  Divider,
} from '@mui/material';
import { 
  Lock as LockIcon, 
  LockOpen as LockOpenIcon,
  Edit as EditIcon,
  Save as SaveIcon,
  Cancel as CancelIcon,
  CheckCircle as CheckCircleIcon,
  Search as SearchIcon,
  Email as EmailIcon,
  Add as AddIcon,
} from '@mui/icons-material';
import { debugAPI, adminGroupsAPI } from '../services/api';

interface User {
  id: number;
  username: string;
  email: string;
  nome: string;
  cognome: string;
  role: string;
  department: string;
  active: boolean;
  must_change_password?: boolean;
  created_at: string;
  employee_deleted?: boolean; // Indica se il dipendente associato è eliminato
}

interface EnabledUser {
  user_id: number;
  username: string;
  email: string;
  name: string;
  password: string;
}

interface TabPanelProps {
  children?: React.ReactNode;
  index: number;
  value: number;
}

function TabPanel(props: TabPanelProps) {
  const { children, value, index, ...other } = props;
  return (
    <div
      role="tabpanel"
      hidden={value !== index}
      id={`users-tabpanel-${index}`}
      aria-labelledby={`users-tab-${index}`}
      {...other}
    >
      {value === index && <Box sx={{ p: 3 }}>{children}</Box>}
    </div>
  );
}

const UserManagement: React.FC = () => {
  const [users, setUsers] = useState<User[]>([]);
  const [selectedUserIds, setSelectedUserIds] = useState<number[]>([]);
  const [loading, setLoading] = useState(true);
  const [enabling, setEnabling] = useState(false);
  const [sendingCreds, setSendingCreds] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [tabValue, setTabValue] = useState(0);
  
  // Dialog per mostrare le credenziali generate
  const [credentialsDialog, setCredentialsDialog] = useState(false);
  const [generatedCredentials, setGeneratedCredentials] = useState<EnabledUser[]>([]);

  // Stati per editing inline
  const [editingUserId, setEditingUserId] = useState<number | null>(null);
  const [editUsername, setEditUsername] = useState('');
  const [editEmail, setEditEmail] = useState('');
  const [updating, setUpdating] = useState(false);

  // Stati per ricerca e paginazione
  const [searchFilters, setSearchFilters] = useState({
    username: '',
    email: '',
    nome: '',
    cognome: '',
    department: '',
    employeeDeleted: '', // 'all', 'deleted', 'not_deleted'
  });
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(50);

  // Stati per filtro gruppo
  const [adminGroups, setAdminGroups] = useState<Array<{ id: number; name: string; description?: string }>>([]);
  const [selectedGroupId, setSelectedGroupId] = useState<number | ''>('');
  const [groupUserIds, setGroupUserIds] = useState<Set<number>>(new Set());
  const [loadingGroups, setLoadingGroups] = useState(false);

  // Stati per dialog creazione utente
  const [createUserDialogOpen, setCreateUserDialogOpen] = useState(false);
  const [creatingUser, setCreatingUser] = useState(false);
  const [createUserFormData, setCreateUserFormData] = useState({
    username: '',
    email: '',
    password: '',
    codice_fiscale: '',
    nome: '',
    cognome: '',
    role: 'employee' as 'admin' | 'manager' | 'employee',
    department: '',
    is_super_admin: false,
    enable_immediately: false,
  });
  const [createUserErrors, setCreateUserErrors] = useState<Record<string, string>>({});

  useEffect(() => {
    loadUsers();
    loadAdminGroups();
  }, []);

  useEffect(() => {
    if (selectedGroupId) {
      loadGroupUserIds(selectedGroupId);
    } else {
      setGroupUserIds(new Set());
    }
  }, [selectedGroupId]);

  const loadUsers = async () => {
    try {
      setLoading(true);
      setError('');
      
      const response = await debugAPI.getAllUsersIncludingInactive();
      
      let usersArray: User[] = [];
      
      if (response && response.users) {
        usersArray = Array.isArray(response.users) ? response.users : [];
      } else if (Array.isArray(response)) {
        usersArray = response;
      }
      
      setUsers(usersArray);
    } catch (err: any) {
      setError(err.response?.data?.error || err.message || 'Errore nel caricamento utenti');
    } finally {
      setLoading(false);
    }
  };

  const loadAdminGroups = async () => {
    try {
      setLoadingGroups(true);
      const response = await adminGroupsAPI.getAllGroups();
      const groups = response.groups || response || [];
      setAdminGroups(groups);
    } catch (err: any) {
      console.error('Errore caricamento gruppi:', err);
      // Non mostriamo errore, semplicemente non filtriamo per gruppo
    } finally {
      setLoadingGroups(false);
    }
  };

  const loadGroupUserIds = async (groupId: number) => {
    try {
      setLoadingGroups(true);
      const response = await adminGroupsAPI.getGroupEmployees(groupId);
      const employees = response.employees || response || [];
      
      // Estrai i user_id dai dipendenti
      const userIds = new Set<number>();
      employees.forEach((emp: any) => {
        if (emp.user_id) {
          userIds.add(emp.user_id);
        }
      });
      
      setGroupUserIds(userIds);
    } catch (err: any) {
      console.error('Errore caricamento dipendenti gruppo:', err);
      setGroupUserIds(new Set());
    } finally {
      setLoadingGroups(false);
    }
  };

  const handleSelectUser = (userId: number) => {
    setSelectedUserIds(prev => 
      prev.includes(userId)
        ? prev.filter(id => id !== userId)
        : [...prev, userId]
    );
  };

  const handleSelectAll = (isActiveTab: boolean) => {
    let targetUsers: number[];
    if (isActiveTab) {
      targetUsers = paginatedActiveUsers.map(u => u.id);
    } else {
      targetUsers = inactiveUsers.map(u => u.id);
    }
    
    const allSelected = targetUsers.length > 0 && targetUsers.every(id => selectedUserIds.includes(id));
    
    setSelectedUserIds(prev => 
      allSelected ? prev.filter(id => !targetUsers.includes(id)) : [...new Set([...prev, ...targetUsers])]
    );
  };

  const handleEnableUsers = async () => {
    if (selectedUserIds.length === 0) {
      setError('Seleziona almeno un utente da abilitare');
      return;
    }

    try {
      setEnabling(true);
      setError('');
      
      const response = await debugAPI.enableUsers(selectedUserIds);
      
      setGeneratedCredentials(response.enabled_users || []);
      setCredentialsDialog(true);
      
      await loadUsers();
      setSelectedUserIds([]);
      
      setSuccess(`Abilitati ${response.enabled_users?.length || 0} utenti con successo! Le credenziali sono state inviate via email.`);
    } catch (err: any) {
      setError(err.response?.data?.error || err.message || 'Errore durante l\'abilitazione degli utenti');
    } finally {
      setEnabling(false);
    }
  };

  const handleDisableUsers = async () => {
    if (selectedUserIds.length === 0) {
      setError('Seleziona almeno un utente da disabilitare');
      return;
    }

    try {
      setUpdating(true);
      setError('');
      
      await debugAPI.disableUsers(selectedUserIds);
      
      await loadUsers();
      setSelectedUserIds([]);
      
      setSuccess(`Disabilitati ${selectedUserIds.length} utenti con successo!`);
    } catch (err: any) {
      setError(err.response?.data?.error || err.message || 'Errore durante la disabilitazione degli utenti');
    } finally {
      setUpdating(false);
    }
  };

  const handleSendTempCredentials = async () => {
    if (selectedUserIds.length === 0) {
      setError('Seleziona almeno un utente');
      return;
    }

    try {
      setSendingCreds(true);
      setError('');
      
      const response = await debugAPI.sendTempCredentials(selectedUserIds);
      
      setGeneratedCredentials(response.credentials || []);
      setCredentialsDialog(true);
      
      await loadUsers();
      setSelectedUserIds([]);
      
      setSuccess(`Inviate credenziali a ${response.credentials?.length || 0} utenti con successo!`);
    } catch (err: any) {
      setError(err.response?.data?.error || err.message || 'Errore durante l\'invio delle credenziali');
    } finally {
      setSendingCreds(false);
    }
  };

  const handleCloseCredentialsDialog = () => {
    setCredentialsDialog(false);
    setGeneratedCredentials([]);
  };

  const handleCloseSuccess = () => {
    setSuccess('');
  };

  const handleTabChange = (_event: React.SyntheticEvent, newValue: number) => {
    setTabValue(newValue);
    setSelectedUserIds([]);
    setEditingUserId(null); // Chiudi editing quando cambi tab
    setPage(0); // Reset alla prima pagina quando si cambia tab
  };

  const handleSelectAllByGroup = () => {
    if (selectedGroupId === '') {
      // Se non c'è filtro gruppo, seleziona tutti come prima
      handleSelectAll(tabValue === 0);
      return;
    }

    // Seleziona solo gli utenti del gruppo nella pagina corrente
    const targetUsers = tabValue === 0 ? paginatedActiveUsers : paginatedInactiveUsers;
    const groupUsers = targetUsers.filter(u => groupUserIds.has(u.id));
    const groupUserIdsArray = groupUsers.map(u => u.id);
    
    const allSelected = groupUserIdsArray.length > 0 && groupUserIdsArray.every(id => selectedUserIds.includes(id));
    
    setSelectedUserIds(prev => 
      allSelected 
        ? prev.filter(id => !groupUserIdsArray.includes(id))
        : [...new Set([...prev, ...groupUserIdsArray])]
    );
  };

  // Funzioni per editing inline
  const handleStartEdit = (user: User) => {
    setEditingUserId(user.id);
    setEditUsername(user.username);
    setEditEmail(user.email);
  };

  const handleCancelEdit = () => {
    setEditingUserId(null);
    setEditUsername('');
    setEditEmail('');
  };

  const handleSaveEdit = async (userId: number) => {
    if (!editUsername.trim() || !editEmail.trim()) {
      setError('Username ed email sono obbligatori');
      return;
    }

    try {
      setUpdating(true);
      setError('');

      const updates: any = {};
      if (editUsername !== users.find(u => u.id === userId)?.username) {
        updates.username = editUsername;
      }
      if (editEmail !== users.find(u => u.id === userId)?.email) {
        updates.email = editEmail;
      }

      if (Object.keys(updates).length > 0) {
        await debugAPI.updateUser(userId, updates);
        setSuccess('Utente aggiornato con successo!');
        await loadUsers();
      }

      setEditingUserId(null);
      setEditUsername('');
      setEditEmail('');
    } catch (err: any) {
      setError(err.response?.data?.error || err.message || 'Errore durante l\'aggiornamento');
    } finally {
      setUpdating(false);
    }
  };

  const handleToggleActive = async (user: User) => {
    try {
      setUpdating(true);
      setError('');
      
      await debugAPI.updateUser(user.id, { active: !user.active });
      setSuccess(`Utente ${!user.active ? 'abilitato' : 'disabilitato'} con successo!`);
      await loadUsers();
      
      // Se l'utente è stato disabilitato, rimuovilo dalla selezione
      if (!user.active) {
        setSelectedUserIds(prev => prev.filter(id => id !== user.id));
      }
    } catch (err: any) {
      setError(err.response?.data?.error || err.message || 'Errore durante l\'operazione');
    } finally {
      setUpdating(false);
    }
  };

  const validateCreateUserForm = (): boolean => {
    const errors: Record<string, string> = {};

    if (!createUserFormData.username.trim()) {
      errors.username = 'Username obbligatorio';
    }
    if (!createUserFormData.email.trim()) {
      errors.email = 'Email obbligatoria';
    } else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(createUserFormData.email)) {
      errors.email = 'Formato email non valido';
    }
    if (!createUserFormData.password) {
      errors.password = 'Password obbligatoria';
    } else if (createUserFormData.password.length < 6) {
      errors.password = 'La password deve essere di almeno 6 caratteri';
    }
    if (!createUserFormData.codice_fiscale.trim()) {
      errors.codice_fiscale = 'Codice fiscale obbligatorio';
    } else if (createUserFormData.codice_fiscale.length !== 16) {
      errors.codice_fiscale = 'Il codice fiscale deve essere di 16 caratteri';
    }
    if (!createUserFormData.nome.trim()) {
      errors.nome = 'Nome obbligatorio';
    }
    if (!createUserFormData.cognome.trim()) {
      errors.cognome = 'Cognome obbligatorio';
    }

    setCreateUserErrors(errors);
    return Object.keys(errors).length === 0;
  };

  const handleCreateUser = async () => {
    if (!validateCreateUserForm()) {
      return;
    }

    try {
      setCreatingUser(true);
      setError('');

      // Prepara i dati per la creazione
      const userData: any = {
        username: createUserFormData.username.trim(),
        email: createUserFormData.email.trim(),
        password: createUserFormData.password,
        codice_fiscale: createUserFormData.codice_fiscale.trim().toUpperCase(),
        nome: createUserFormData.nome.trim(),
        cognome: createUserFormData.cognome.trim(),
        role: createUserFormData.role,
        department: createUserFormData.department.trim() || undefined,
        is_super_admin: createUserFormData.is_super_admin,
        ore_lavoro: 0,
        giorni_lavoro: 0,
        data_apertura_posizione: new Date().toISOString().split('T')[0],
      };

      // Se non si abilita subito, crea l'utente disabilitato
      if (!createUserFormData.enable_immediately) {
        userData.active = false;
      }

      // Crea l'utente
      const response = await debugAPI.createUser(userData);
      const newUserId = response.user?.id;

      if (!newUserId) {
        throw new Error('Errore: ID utente non restituito');
      }

      // Se "Abilita subito" è selezionato, abilita l'utente e genera credenziali
      if (createUserFormData.enable_immediately) {
        const enableResponse = await debugAPI.enableUsers([newUserId]);
        setGeneratedCredentials(enableResponse.enabled_users || []);
        setCredentialsDialog(true);
        setSuccess(`Utente creato e abilitato con successo! Le credenziali sono state inviate via email.`);
      } else {
        setSuccess(`Utente creato con successo! L'utente è disabilitato e può essere abilitato successivamente.`);
      }

      // Ricarica la lista utenti
      await loadUsers();

      // Chiudi il dialog e resetta il form
      setCreateUserDialogOpen(false);
      setCreateUserFormData({
        username: '',
        email: '',
        password: '',
        codice_fiscale: '',
        nome: '',
        cognome: '',
        role: 'employee',
        department: '',
        is_super_admin: false,
        enable_immediately: false,
      });
      setCreateUserErrors({});
    } catch (err: any) {
      setError(err.response?.data?.error || err.message || 'Errore durante la creazione dell\'utente');
    } finally {
      setCreatingUser(false);
    }
  };

  // Filtra utenti in base ai criteri di ricerca e al gruppo selezionato
  const filteredUsers = useMemo(() => {
    return users.filter(user => {
      // Filtro per gruppo (se selezionato)
      const matchesGroup = selectedGroupId === '' || groupUserIds.has(user.id);
      
      const matchesUsername = searchFilters.username === '' || 
        user.username.toLowerCase().includes(searchFilters.username.toLowerCase());
      const matchesEmail = searchFilters.email === '' || 
        user.email.toLowerCase().includes(searchFilters.email.toLowerCase());
      const matchesNome = searchFilters.nome === '' || 
        user.nome?.toLowerCase().includes(searchFilters.nome.toLowerCase());
      const matchesCognome = searchFilters.cognome === '' || 
        user.cognome?.toLowerCase().includes(searchFilters.cognome.toLowerCase());
      const matchesDepartment = searchFilters.department === '' || 
        (user.department && user.department.toLowerCase().includes(searchFilters.department.toLowerCase()));
      const matchesEmployeeDeleted = searchFilters.employeeDeleted === '' || 
        searchFilters.employeeDeleted === 'all' ||
        (searchFilters.employeeDeleted === 'deleted' && user.employee_deleted === true) ||
        (searchFilters.employeeDeleted === 'not_deleted' && (user.employee_deleted === false || user.employee_deleted === undefined));
      
      return matchesGroup && matchesUsername && matchesEmail && matchesNome && matchesCognome && matchesDepartment && matchesEmployeeDeleted;
    });
  }, [users, searchFilters, selectedGroupId, groupUserIds]);

  const activeUsers = filteredUsers.filter(u => u.active);
  const inactiveUsers = filteredUsers.filter(u => !u.active);

  // Paginazione
  const paginatedActiveUsers = useMemo(() => {
    const start = page * rowsPerPage;
    const end = start + rowsPerPage;
    return activeUsers.slice(start, end);
  }, [activeUsers, page, rowsPerPage]);

  const paginatedInactiveUsers = useMemo(() => {
    const start = page * rowsPerPage;
    const end = start + rowsPerPage;
    return inactiveUsers.slice(start, end);
  }, [inactiveUsers, page, rowsPerPage]);
  
  const allActiveSelected = useMemo(() => {
    const pageActiveIds = paginatedActiveUsers.map(u => u.id);
    return pageActiveIds.length > 0 && pageActiveIds.every(id => selectedUserIds.includes(id));
  }, [paginatedActiveUsers, selectedUserIds]);
  
  const allInactiveSelected = useMemo(() => {
    const pageInactiveIds = paginatedInactiveUsers.map(u => u.id);
    return pageInactiveIds.length > 0 && pageInactiveIds.every(id => selectedUserIds.includes(id));
  }, [paginatedInactiveUsers, selectedUserIds]);

  const handleSearchChange = (field: keyof typeof searchFilters, value: string) => {
    setSearchFilters(prev => ({ ...prev, [field]: value }));
    setPage(0); // Reset alla prima pagina quando si cerca
  };

  const handleChangePage = (event: unknown, newPage: number) => {
    setPage(newPage);
  };

  const handleChangeRowsPerPage = (event: React.ChangeEvent<HTMLInputElement>) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };

  const renderUserTable = (usersToShow: User[], showCheckbox: boolean = false) => {
    if (usersToShow.length === 0) {
      const colSpan = showCheckbox ? 12 : 11;
      return (
        <TableRow>
          <TableCell colSpan={colSpan} align="center">
            <Typography variant="body2" color="text.secondary" sx={{ py: 3 }}>
              Nessun utente trovato
            </Typography>
          </TableCell>
        </TableRow>
      );
    }

    return usersToShow.map((user) => (
      <TableRow 
        key={user.id}
        hover
        sx={{ cursor: showCheckbox ? 'pointer' : 'default' }}
      >
        {showCheckbox && (
          <TableCell padding="checkbox">
            <Checkbox
              checked={selectedUserIds.includes(user.id)}
              onChange={() => handleSelectUser(user.id)}
              onClick={(e) => e.stopPropagation()}
            />
          </TableCell>
        )}
        <TableCell>{user.id}</TableCell>
        
        {/* Username con editing */}
        <TableCell>
          {editingUserId === user.id ? (
            <TextField
              size="small"
              value={editUsername}
              onChange={(e) => setEditUsername(e.target.value)}
              disabled={updating}
              sx={{ width: '150px' }}
            />
          ) : (
            <Box display="flex" alignItems="center" gap={1}>
              {!user.active ? (
                <LockIcon fontSize="small" color="disabled" />
              ) : (
                <LockOpenIcon fontSize="small" color="success" />
              )}
              {user.username}
            </Box>
          )}
        </TableCell>
        
        <TableCell>{user.nome || '-'}</TableCell>
        <TableCell>{user.cognome || '-'}</TableCell>
        
        {/* Email con editing */}
        <TableCell>
          {editingUserId === user.id ? (
            <TextField
              size="small"
              value={editEmail}
              onChange={(e) => setEditEmail(e.target.value)}
              disabled={updating}
              sx={{ width: '200px' }}
            />
          ) : (
            user.email
          )}
        </TableCell>
        
        <TableCell>{user.role || '-'}</TableCell>
        <TableCell>{user.department || '-'}</TableCell>
        
        {/* Stato con toggle */}
        <TableCell>
          <Box display="flex" alignItems="center" gap={1} flexWrap="wrap">
            {user.active ? (
              <Chip 
                label="Abilitato" 
                color="success" 
                size="small"
                sx={{ fontWeight: 'bold' }}
              />
            ) : (
              <Chip 
                label="Disabilitato" 
                color="error" 
                size="small"
                sx={{ fontWeight: 'bold' }}
              />
            )}
            {user.employee_deleted && (
              <Chip 
                label="Dipendente Eliminato" 
                color="warning" 
                size="small"
                sx={{ fontWeight: 'bold' }}
              />
            )}
            <Tooltip title={user.active ? 'Disabilita utente' : 'Abilita utente'}>
              <IconButton
                size="small"
                onClick={() => handleToggleActive(user)}
                disabled={updating}
                color={user.active ? 'error' : 'success'}
              >
                {user.active ? <LockIcon /> : <LockOpenIcon />}
              </IconButton>
            </Tooltip>
          </Box>
        </TableCell>
        
        <TableCell>
          {user.must_change_password && (
            <Chip 
              label="Cambio Password" 
              color="warning" 
              size="small"
            />
          )}
        </TableCell>
        
        <TableCell>{new Date(user.created_at).toLocaleDateString('it-IT')}</TableCell>
        
        {/* Azioni */}
        <TableCell>
          {editingUserId === user.id ? (
            <Box display="flex" gap={0.5}>
              <Tooltip title="Salva">
                <IconButton
                  size="small"
                  color="success"
                  onClick={() => handleSaveEdit(user.id)}
                  disabled={updating}
                >
                  <SaveIcon />
                </IconButton>
              </Tooltip>
              <Tooltip title="Annulla">
                <IconButton
                  size="small"
                  color="error"
                  onClick={handleCancelEdit}
                  disabled={updating}
                >
                  <CancelIcon />
                </IconButton>
              </Tooltip>
            </Box>
          ) : (
            <Tooltip title="Modifica username/email">
              <IconButton
                size="small"
                onClick={() => handleStartEdit(user)}
                disabled={updating}
              >
                <EditIcon />
              </IconButton>
            </Tooltip>
          )}
        </TableCell>
      </TableRow>
    ));
  };

  if (loading) {
    return (
      <Container maxWidth="xl" sx={{ mt: 4, mb: 4 }}>
        <Box display="flex" justifyContent="center" alignItems="center" minHeight="400px">
          <CircularProgress />
        </Box>
      </Container>
    );
  }

  return (
    <Container maxWidth={false} sx={{ mt: 4, mb: 4, px: 3 }}>
      <Box display="flex" justifyContent="space-between" alignItems="center" mb={3}>
        <Typography variant="h4" component="h1">
          Gestione Utenti
        </Typography>
        <Box display="flex" gap={2}>
          <Button
            variant="contained"
            startIcon={<AddIcon />}
            onClick={() => {
              setCreateUserDialogOpen(true);
              setCreateUserFormData({
                username: '',
                email: '',
                password: '',
                codice_fiscale: '',
                nome: '',
                cognome: '',
                role: 'employee',
                department: '',
                is_super_admin: false,
                enable_immediately: false,
              });
              setCreateUserErrors({});
            }}
          >
            Nuovo Utente
          </Button>
          <Button variant="outlined" onClick={loadUsers} disabled={loading}>
            Ricarica
          </Button>
        </Box>
      </Box>

      {/* Filtro per gruppo */}
      <Card sx={{ mb: 3, p: 2 }}>
        <Box display="flex" alignItems="center" gap={2}>
          <FormControl size="small" sx={{ minWidth: 300 }}>
            <InputLabel>Filtra per Gruppo Admin</InputLabel>
            <Select
              value={selectedGroupId}
              onChange={(e) => {
                const value = e.target.value;
                setSelectedGroupId(value === '' ? '' : Number(value));
                setPage(0); // Reset alla prima pagina quando si cambia gruppo
                setSelectedUserIds([]); // Deseleziona tutti quando si cambia gruppo
              }}
              label="Filtra per Gruppo Admin"
              disabled={loadingGroups}
            >
              <MenuItem value="">
                <em>Tutti i gruppi</em>
              </MenuItem>
              {adminGroups.map((group) => (
                <MenuItem key={group.id} value={group.id}>
                  {group.name}
                  {group.description && ` - ${group.description}`}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
          {selectedGroupId && (
            <Chip
              label={`${groupUserIds.size} utenti nel gruppo`}
              color="primary"
              size="small"
            />
          )}
          {loadingGroups && <CircularProgress size={20} />}
        </Box>
      </Card>

      {error && (
        <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError('')}>
          {error}
        </Alert>
      )}

      <Card>
        <Box sx={{ borderBottom: 1, borderColor: 'divider' }}>
          <Tabs value={tabValue} onChange={handleTabChange}>
            <Tab 
              label={
                <Box display="flex" alignItems="center" gap={1}>
                  <LockOpenIcon fontSize="small" />
                  <span>Utenti Abilitati</span>
                  {activeUsers.length > 0 && (
                    <Chip label={activeUsers.length} size="small" color="primary" />
                  )}
                </Box>
              }
            />
            <Tab 
              label={
                <Box display="flex" alignItems="center" gap={1}>
                  <LockIcon fontSize="small" />
                  <span>Utenti Disabilitati</span>
                  {inactiveUsers.length > 0 && (
                    <Chip label={inactiveUsers.length} size="small" color="error" />
                  )}
                </Box>
              }
            />
          </Tabs>
        </Box>

        <TabPanel value={tabValue} index={0}>
          <Box display="flex" justifyContent="space-between" alignItems="center" mb={2}>
            <Box>
              <Typography variant="h6" gutterBottom>
                Utenti Abilitati ({activeUsers.length})
              </Typography>
              <Typography variant="body2" color="text.secondary">
                Questi utenti possono accedere al sistema.
              </Typography>
            </Box>
            <Box display="flex" gap={2}>
              <Button
                variant="outlined"
                onClick={selectedGroupId ? handleSelectAllByGroup : () => handleSelectAll(true)}
                disabled={paginatedActiveUsers.length === 0}
              >
                {selectedGroupId 
                  ? (paginatedActiveUsers.filter(u => groupUserIds.has(u.id)).every(u => selectedUserIds.includes(u.id)) 
                      ? 'Deseleziona Gruppo' 
                      : 'Seleziona Gruppo')
                  : (allActiveSelected ? 'Deseleziona Tutti' : 'Seleziona Tutti')
                }
              </Button>
              <Button
                variant="contained"
                color="error"
                onClick={handleDisableUsers}
                disabled={selectedUserIds.length === 0 || updating}
                startIcon={<LockIcon />}
              >
                {updating ? <CircularProgress size={20} /> : `Disabilita (${selectedUserIds.length})`}
              </Button>
              <Button
                variant="contained"
                color="primary"
                onClick={handleSendTempCredentials}
                disabled={selectedUserIds.length === 0 || sendingCreds}
                startIcon={<EmailIcon />}
              >
                {sendingCreds ? <CircularProgress size={20} /> : `Invia Credenziali (${selectedUserIds.length})`}
              </Button>
            </Box>
          </Box>

          <TableContainer component={Paper}>
            <Table stickyHeader size="small">
              <TableHead>
                <TableRow>
                  <TableCell padding="checkbox" sx={{ backgroundColor: 'background.paper' }}>
                    <Checkbox
                      indeterminate={selectedUserIds.length > 0 && !allActiveSelected && paginatedActiveUsers.length > 0}
                      checked={allActiveSelected}
                      onChange={() => handleSelectAll(true)}
                      disabled={paginatedActiveUsers.length === 0}
                    />
                  </TableCell>
                  <TableCell sx={{ fontWeight: 'bold', backgroundColor: 'background.paper', minWidth: 80 }}>ID</TableCell>
                  <TableCell sx={{ fontWeight: 'bold', backgroundColor: 'background.paper', minWidth: 150 }}>
                    <TextField
                      placeholder="Cerca username..."
                      size="small"
                      value={searchFilters.username}
                      onChange={(e) => handleSearchChange('username', e.target.value)}
                      InputProps={{
                        startAdornment: (
                          <InputAdornment position="start">
                            <SearchIcon fontSize="small" />
                          </InputAdornment>
                        ),
                      }}
                      sx={{ width: '100%' }}
                    />
                  </TableCell>
                  <TableCell sx={{ fontWeight: 'bold', backgroundColor: 'background.paper', minWidth: 150 }}>
                    <TextField
                      placeholder="Cerca nome..."
                      size="small"
                      value={searchFilters.nome}
                      onChange={(e) => handleSearchChange('nome', e.target.value)}
                      InputProps={{
                        startAdornment: (
                          <InputAdornment position="start">
                            <SearchIcon fontSize="small" />
                          </InputAdornment>
                        ),
                      }}
                      sx={{ width: '100%' }}
                    />
                  </TableCell>
                  <TableCell sx={{ fontWeight: 'bold', backgroundColor: 'background.paper', minWidth: 150 }}>
                    <TextField
                      placeholder="Cerca cognome..."
                      size="small"
                      value={searchFilters.cognome}
                      onChange={(e) => handleSearchChange('cognome', e.target.value)}
                      InputProps={{
                        startAdornment: (
                          <InputAdornment position="start">
                            <SearchIcon fontSize="small" />
                          </InputAdornment>
                        ),
                      }}
                      sx={{ width: '100%' }}
                    />
                  </TableCell>
                  <TableCell sx={{ fontWeight: 'bold', backgroundColor: 'background.paper', minWidth: 200 }}>
                    <TextField
                      placeholder="Cerca email..."
                      size="small"
                      value={searchFilters.email}
                      onChange={(e) => handleSearchChange('email', e.target.value)}
                      InputProps={{
                        startAdornment: (
                          <InputAdornment position="start">
                            <SearchIcon fontSize="small" />
                          </InputAdornment>
                        ),
                      }}
                      sx={{ width: '100%' }}
                    />
                  </TableCell>
                  <TableCell sx={{ fontWeight: 'bold', backgroundColor: 'background.paper', minWidth: 120 }}>Ruolo</TableCell>
                  <TableCell sx={{ fontWeight: 'bold', backgroundColor: 'background.paper', minWidth: 150 }}>
                    <TextField
                      placeholder="Cerca dipartimento..."
                      size="small"
                      value={searchFilters.department}
                      onChange={(e) => handleSearchChange('department', e.target.value)}
                      InputProps={{
                        startAdornment: (
                          <InputAdornment position="start">
                            <SearchIcon fontSize="small" />
                          </InputAdornment>
                        ),
                      }}
                      sx={{ width: '100%' }}
                    />
                  </TableCell>
                  <TableCell sx={{ fontWeight: 'bold', backgroundColor: 'background.paper', minWidth: 120 }}>Stato</TableCell>
                  <TableCell sx={{ fontWeight: 'bold', backgroundColor: 'background.paper', minWidth: 120 }}>Cambio Password</TableCell>
                  <TableCell sx={{ fontWeight: 'bold', backgroundColor: 'background.paper', minWidth: 100 }}>Creato</TableCell>
                  <TableCell sx={{ fontWeight: 'bold', backgroundColor: 'background.paper', minWidth: 100 }}>Azioni</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {renderUserTable(paginatedActiveUsers, true)}
              </TableBody>
            </Table>
          </TableContainer>
          <TablePagination
            component="div"
            count={activeUsers.length}
            page={page}
            onPageChange={handleChangePage}
            rowsPerPage={rowsPerPage}
            onRowsPerPageChange={handleChangeRowsPerPage}
            rowsPerPageOptions={[10, 25, 50, 100]}
            labelRowsPerPage="Righe per pagina:"
            labelDisplayedRows={({ from, to, count }) => `${from}-${to} di ${count}`}
          />
        </TabPanel>

        <TabPanel value={tabValue} index={1}>
          <Box display="flex" justifyContent="space-between" alignItems="center" mb={2}>
            <Box>
              <Typography variant="h6" gutterBottom>
                Utenti Disabilitati ({inactiveUsers.length})
              </Typography>
              <Typography variant="body2" color="text.secondary">
                Seleziona gli utenti da abilitare. Verranno generate credenziali temporanee e inviate via email.
              </Typography>
            </Box>
            <Box display="flex" gap={2}>
              <Button
                variant="outlined"
                onClick={selectedGroupId ? handleSelectAllByGroup : () => handleSelectAll(false)}
                disabled={inactiveUsers.length === 0}
              >
                {selectedGroupId 
                  ? (paginatedInactiveUsers.filter(u => groupUserIds.has(u.id)).every(u => selectedUserIds.includes(u.id)) 
                      ? 'Deseleziona Gruppo' 
                      : 'Seleziona Gruppo')
                  : (allInactiveSelected ? 'Deseleziona Tutti' : 'Seleziona Tutti')
                }
              </Button>
              <Button
                variant="contained"
                color="success"
                onClick={handleEnableUsers}
                disabled={selectedUserIds.length === 0 || enabling}
                startIcon={<CheckCircleIcon />}
              >
                {enabling ? <CircularProgress size={20} /> : `Abilita Selezionati (${selectedUserIds.length})`}
              </Button>
            </Box>
          </Box>

          <TableContainer component={Paper}>
            <Table stickyHeader size="small">
              <TableHead>
                <TableRow>
                  <TableCell padding="checkbox" sx={{ backgroundColor: 'background.paper' }}>
                    <Checkbox
                      indeterminate={selectedUserIds.length > 0 && !allInactiveSelected}
                      checked={allInactiveSelected}
                      onChange={() => handleSelectAll(false)}
                      disabled={inactiveUsers.length === 0}
                    />
                  </TableCell>
                  <TableCell sx={{ fontWeight: 'bold', backgroundColor: 'background.paper', minWidth: 80 }}>ID</TableCell>
                  <TableCell sx={{ fontWeight: 'bold', backgroundColor: 'background.paper', minWidth: 150 }}>
                    <TextField
                      placeholder="Cerca username..."
                      size="small"
                      value={searchFilters.username}
                      onChange={(e) => handleSearchChange('username', e.target.value)}
                      InputProps={{
                        startAdornment: (
                          <InputAdornment position="start">
                            <SearchIcon fontSize="small" />
                          </InputAdornment>
                        ),
                      }}
                      sx={{ width: '100%' }}
                    />
                  </TableCell>
                  <TableCell sx={{ fontWeight: 'bold', backgroundColor: 'background.paper', minWidth: 150 }}>
                    <TextField
                      placeholder="Cerca nome..."
                      size="small"
                      value={searchFilters.nome}
                      onChange={(e) => handleSearchChange('nome', e.target.value)}
                      InputProps={{
                        startAdornment: (
                          <InputAdornment position="start">
                            <SearchIcon fontSize="small" />
                          </InputAdornment>
                        ),
                      }}
                      sx={{ width: '100%' }}
                    />
                  </TableCell>
                  <TableCell sx={{ fontWeight: 'bold', backgroundColor: 'background.paper', minWidth: 150 }}>
                    <TextField
                      placeholder="Cerca cognome..."
                      size="small"
                      value={searchFilters.cognome}
                      onChange={(e) => handleSearchChange('cognome', e.target.value)}
                      InputProps={{
                        startAdornment: (
                          <InputAdornment position="start">
                            <SearchIcon fontSize="small" />
                          </InputAdornment>
                        ),
                      }}
                      sx={{ width: '100%' }}
                    />
                  </TableCell>
                  <TableCell sx={{ fontWeight: 'bold', backgroundColor: 'background.paper', minWidth: 200 }}>
                    <TextField
                      placeholder="Cerca email..."
                      size="small"
                      value={searchFilters.email}
                      onChange={(e) => handleSearchChange('email', e.target.value)}
                      InputProps={{
                        startAdornment: (
                          <InputAdornment position="start">
                            <SearchIcon fontSize="small" />
                          </InputAdornment>
                        ),
                      }}
                      sx={{ width: '100%' }}
                    />
                  </TableCell>
                  <TableCell sx={{ fontWeight: 'bold', backgroundColor: 'background.paper', minWidth: 120 }}>Ruolo</TableCell>
                  <TableCell sx={{ fontWeight: 'bold', backgroundColor: 'background.paper', minWidth: 150 }}>
                    <TextField
                      placeholder="Cerca dipartimento..."
                      size="small"
                      value={searchFilters.department}
                      onChange={(e) => handleSearchChange('department', e.target.value)}
                      InputProps={{
                        startAdornment: (
                          <InputAdornment position="start">
                            <SearchIcon fontSize="small" />
                          </InputAdornment>
                        ),
                      }}
                      sx={{ width: '100%' }}
                    />
                  </TableCell>
                  <TableCell sx={{ fontWeight: 'bold', backgroundColor: 'background.paper', minWidth: 200 }}>
                    <FormControl fullWidth size="small">
                      <InputLabel>Filtra per dipendente</InputLabel>
                      <Select
                        value={searchFilters.employeeDeleted || 'all'}
                        onChange={(e) => handleSearchChange('employeeDeleted', e.target.value)}
                        label="Filtra per dipendente"
                      >
                        <MenuItem value="all">Tutti</MenuItem>
                        <MenuItem value="deleted">Dipendente Eliminato</MenuItem>
                        <MenuItem value="not_deleted">Dipendente Non Eliminato</MenuItem>
                      </Select>
                    </FormControl>
                  </TableCell>
                  <TableCell sx={{ fontWeight: 'bold', backgroundColor: 'background.paper', minWidth: 120 }}>Stato</TableCell>
                  <TableCell sx={{ fontWeight: 'bold', backgroundColor: 'background.paper', minWidth: 100 }}>Creato</TableCell>
                  <TableCell sx={{ fontWeight: 'bold', backgroundColor: 'background.paper', minWidth: 100 }}>Azioni</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {renderUserTable(paginatedInactiveUsers, true)}
              </TableBody>
            </Table>
          </TableContainer>
          <TablePagination
            component="div"
            count={inactiveUsers.length}
            page={page}
            onPageChange={handleChangePage}
            rowsPerPage={rowsPerPage}
            onRowsPerPageChange={handleChangeRowsPerPage}
            rowsPerPageOptions={[10, 25, 50, 100]}
            labelRowsPerPage="Righe per pagina:"
            labelDisplayedRows={({ from, to, count }) => `${from}-${to} di ${count}`}
          />
        </TabPanel>
      </Card>

      {/* Dialog per mostrare le credenziali generate */}
      <Dialog 
        open={credentialsDialog} 
        onClose={handleCloseCredentialsDialog}
        maxWidth="md"
        fullWidth
      >
        <DialogTitle>
          <Box display="flex" alignItems="center" gap={2}>
            <CheckCircleIcon color="success" />
            <Typography variant="h6">Utenti Abilitati con Successo</Typography>
          </Box>
        </DialogTitle>
        <DialogContent>
          <Alert severity="info" sx={{ mb: 2 }}>
            Le credenziali sono state inviate via email agli utenti. Salva queste informazioni per riferimento.
          </Alert>
          
          <Typography variant="subtitle2" gutterBottom sx={{ mt: 2 }}>
            Credenziali Generate:
          </Typography>
          
          <TableContainer sx={{ mt: 1 }}>
            <Table size="small">
              <TableHead>
                <TableRow>
                  <TableCell>Username</TableCell>
                  <TableCell>Email</TableCell>
                  <TableCell>Password Temporanea</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {generatedCredentials.map((cred, idx) => (
                  <TableRow key={idx}>
                    <TableCell>
                      <TextField
                        size="small"
                        value={cred.username}
                        fullWidth
                        InputProps={{
                          readOnly: true,
                        }}
                        onClick={(e) => (e.target as HTMLInputElement).select()}
                      />
                    </TableCell>
                    <TableCell>{cred.email}</TableCell>
                    <TableCell>
                      <TextField
                        size="small"
                        value={cred.password}
                        fullWidth
                        InputProps={{
                          readOnly: true,
                        }}
                        onClick={(e) => (e.target as HTMLInputElement).select()}
                      />
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseCredentialsDialog} variant="contained">
            Chiudi
          </Button>
        </DialogActions>
      </Dialog>

      {/* Dialog per creazione nuovo utente */}
      <Dialog
        open={createUserDialogOpen}
        onClose={() => {
          if (!creatingUser) {
            setCreateUserDialogOpen(false);
            setCreateUserFormData({
              username: '',
              email: '',
              password: '',
              codice_fiscale: '',
              nome: '',
              cognome: '',
              role: 'employee',
              department: '',
              is_super_admin: false,
              enable_immediately: false,
            });
            setCreateUserErrors({});
          }
        }}
        maxWidth="md"
        fullWidth
      >
        <DialogTitle>
          <Typography variant="h6">Crea Nuovo Utente</Typography>
        </DialogTitle>
        <DialogContent>
          <Alert severity="info" sx={{ mb: 3 }}>
            Crea un utente che non è associato a un dipendente. Puoi abilitarlo immediatamente o dopo la creazione.
          </Alert>

          <Grid container spacing={2} sx={{ mt: 1 }}>
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                label="Username"
                value={createUserFormData.username}
                onChange={(e) => setCreateUserFormData({ ...createUserFormData, username: e.target.value })}
                error={!!createUserErrors.username}
                helperText={createUserErrors.username}
                required
                disabled={creatingUser}
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                label="Email"
                type="email"
                value={createUserFormData.email}
                onChange={(e) => setCreateUserFormData({ ...createUserFormData, email: e.target.value })}
                error={!!createUserErrors.email}
                helperText={createUserErrors.email}
                required
                disabled={creatingUser}
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                label="Password"
                type="password"
                value={createUserFormData.password}
                onChange={(e) => setCreateUserFormData({ ...createUserFormData, password: e.target.value })}
                error={!!createUserErrors.password}
                helperText={createUserErrors.password || 'Minimo 6 caratteri'}
                required
                disabled={creatingUser}
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                label="Codice Fiscale"
                value={createUserFormData.codice_fiscale}
                onChange={(e) => setCreateUserFormData({ ...createUserFormData, codice_fiscale: e.target.value.toUpperCase() })}
                error={!!createUserErrors.codice_fiscale}
                helperText={createUserErrors.codice_fiscale || '16 caratteri'}
                required
                inputProps={{ maxLength: 16 }}
                disabled={creatingUser}
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                label="Nome"
                value={createUserFormData.nome}
                onChange={(e) => setCreateUserFormData({ ...createUserFormData, nome: e.target.value })}
                error={!!createUserErrors.nome}
                helperText={createUserErrors.nome}
                required
                disabled={creatingUser}
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                label="Cognome"
                value={createUserFormData.cognome}
                onChange={(e) => setCreateUserFormData({ ...createUserFormData, cognome: e.target.value })}
                error={!!createUserErrors.cognome}
                helperText={createUserErrors.cognome}
                required
                disabled={creatingUser}
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <FormControl fullWidth required>
                <InputLabel>Ruolo</InputLabel>
                <Select
                  value={createUserFormData.role}
                  label="Ruolo"
                  onChange={(e) => setCreateUserFormData({ ...createUserFormData, role: e.target.value as 'admin' | 'manager' | 'employee' })}
                  disabled={creatingUser}
                >
                  <MenuItem value="employee">Dipendente</MenuItem>
                  <MenuItem value="manager">Manager</MenuItem>
                  <MenuItem value="admin">Admin</MenuItem>
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                label="Dipartimento"
                value={createUserFormData.department}
                onChange={(e) => setCreateUserFormData({ ...createUserFormData, department: e.target.value })}
                disabled={creatingUser}
              />
            </Grid>
            <Grid item xs={12}>
              <Divider sx={{ my: 1 }} />
            </Grid>
            <Grid item xs={12}>
              <FormControlLabel
                control={
                  <Checkbox
                    checked={createUserFormData.is_super_admin}
                    onChange={(e) => setCreateUserFormData({ ...createUserFormData, is_super_admin: e.target.checked })}
                    disabled={creatingUser}
                  />
                }
                label="Super Admin"
              />
            </Grid>
            <Grid item xs={12}>
              <FormControlLabel
                control={
                  <Checkbox
                    checked={createUserFormData.enable_immediately}
                    onChange={(e) => setCreateUserFormData({ ...createUserFormData, enable_immediately: e.target.checked })}
                    disabled={creatingUser}
                  />
                }
                label="Abilita subito (genera password temporanea e invia email)"
              />
              {createUserFormData.enable_immediately && (
                <Alert severity="info" sx={{ mt: 1 }}>
                  L'utente verrà creato abilitato. Verrà generata una password temporanea e inviata via email.
                </Alert>
              )}
            </Grid>
          </Grid>
        </DialogContent>
        <DialogActions>
          <Button
            onClick={() => {
              setCreateUserDialogOpen(false);
              setCreateUserFormData({
                username: '',
                email: '',
                password: '',
                codice_fiscale: '',
                nome: '',
                cognome: '',
                role: 'employee',
                department: '',
                is_super_admin: false,
                enable_immediately: false,
              });
              setCreateUserErrors({});
            }}
            disabled={creatingUser}
          >
            Annulla
          </Button>
          <Button
            onClick={handleCreateUser}
            variant="contained"
            disabled={creatingUser}
            startIcon={creatingUser ? <CircularProgress size={20} /> : <AddIcon />}
          >
            {creatingUser ? 'Creazione...' : 'Crea Utente'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Snackbar per messaggio di successo */}
      <Snackbar
        open={!!success}
        autoHideDuration={6000}
        onClose={handleCloseSuccess}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
      >
        <Alert onClose={handleCloseSuccess} severity="success" sx={{ width: '100%' }}>
          {success}
        </Alert>
      </Snackbar>
    </Container>
  );
};

export default UserManagement;
